/**
 * Family Vault - NWGSV Command System
 * Phase D.1: Secure Family Legacy Archive
 * Premium Black/Gold Styling
 */
import { useState, useEffect } from 'react';
import { apiCall } from '../utils/api';
import './familyVault.css';

// Types
interface VaultItem {
  id: string;
  title: string;
  description: string;
  category: string;
  item_type: string;
  file_id?: string;
  created_by: string;
  visibility: string;
  tags: string[];
  date_added: string;
  legacy_date?: string;
}

interface VaultCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  item_count: number;
}

interface DashboardData {
  total_items: number;
  photos: number;
  documents: number;
  memories: number;
  legal_records: number;
  financial_records: number;
  categories_count: number;
  recent_activity: any[];
}

export default function FamilyVault({ onBack }: { onBack: () => void }) {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'browse' | 'categories' | 'timeline' | 'secure'>('dashboard');
  
  // Data states
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [items, setItems] = useState<VaultItem[]>([]);
  const [categories, setCategories] = useState<VaultCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    loadDashboard();
    loadCategories();
  }, []);

  useEffect(() => {
    if (activeTab === 'browse') {
      loadItems();
    }
  }, [activeTab, selectedCategory, searchQuery]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/api/family-vault/dashboard');
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await apiCall('/api/family-vault/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const loadItems = async () => {
    try {
      let url = '/api/family-vault/items?';
      if (selectedCategory) url += `category=${encodeURIComponent(selectedCategory)}&`;
      if (searchQuery) url += `search=${encodeURIComponent(searchQuery)}&`;
      
      const response = await apiCall(url);
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAddItem = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('/api/family-vault/items', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      if (response.ok) {
        setShowAddForm(false);
        await loadDashboard();
        await loadItems();
        form.reset();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create item');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDownload = async (itemId: string, title: string) => {
    try {
      const response = await fetch(`/api/family-vault/download/${itemId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = title;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getItemTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      document: '📄',
      photo: '📸',
      video: '🎬',
      audio: '🎵',
      note: '📝',
      memory: '💭',
      legal: '⚖️',
      financial: '💰'
    };
    return icons[type] || '📁';
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>FAMILY VAULT</h1>
          <p style={styles.subtitle}>Secure Family Legacy Archive • Memories • Documents • History</p>
        </div>
        <button style={styles.backButton} onClick={onBack}>
          ← RETURN TO CUBE
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Tab Navigation */}
      <div style={styles.tabBar}>
        {['dashboard', 'browse', 'categories', 'timeline', 'secure'].map(tab => (
          <button
            key={tab}
            style={{
              ...styles.tab,
              ...(activeTab === tab ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab(tab as any)}
          >
            {tab.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={styles.content}>
        {/* DASHBOARD */}
        {activeTab === 'dashboard' && dashboard && (
          <div>
            <div style={styles.dashboardGrid}>
              <div style={styles.card} onClick={() => setActiveTab('browse')}>
                <div style={styles.cardIcon}>🏛️</div>
                <div style={styles.cardNumber}>{dashboard.total_items}</div>
                <div style={styles.cardLabel}>Total Items</div>
              </div>
              <div style={styles.card} onClick={() => { setActiveTab('browse'); setSelectedCategory('Photos'); }}>
                <div style={styles.cardIcon}>📸</div>
                <div style={styles.cardNumber}>{dashboard.photos}</div>
                <div style={styles.cardLabel}>Photos</div>
              </div>
              <div style={styles.card} onClick={() => setActiveTab('browse')}>
                <div style={styles.cardIcon}>💭</div>
                <div style={styles.cardNumber}>{dashboard.memories}</div>
                <div style={styles.cardLabel}>Memories</div>
              </div>
              <div style={styles.card} onClick={() => setActiveTab('secure')}>
                <div style={styles.cardIcon}>⚖️</div>
                <div style={styles.cardNumber}>{dashboard.legal_records}</div>
                <div style={styles.cardLabel}>Legal Records</div>
              </div>
              <div style={styles.card} onClick={() => setActiveTab('secure')}>
                <div style={styles.cardIcon}>💰</div>
                <div style={styles.cardNumber}>{dashboard.financial_records}</div>
                <div style={styles.cardLabel}>Financial Records</div>
              </div>
              <div style={styles.card} onClick={() => setActiveTab('categories')}>
                <div style={styles.cardIcon}>📁</div>
                <div style={styles.cardNumber}>{dashboard.categories_count}</div>
                <div style={styles.cardLabel}>Categories</div>
              </div>
            </div>

            {/* Quick Actions */}
            <div style={styles.quickActions}>
              <h3 style={styles.sectionTitle}>Quick Actions</h3>
              <div style={styles.actionsGrid}>
                <button style={styles.actionButton} onClick={() => setShowAddForm(true)}>
                  <span style={{ fontSize: '24px' }}>💭</span>
                  <span>Add Memory</span>
                </button>
                <button style={styles.actionButton} onClick={() => setShowAddForm(true)}>
                  <span style={{ fontSize: '24px' }}>📸</span>
                  <span>Upload Photo</span>
                </button>
                <button style={styles.actionButton} onClick={() => setShowAddForm(true)}>
                  <span style={{ fontSize: '24px' }}>📄</span>
                  <span>Add Document</span>
                </button>
                <button style={styles.actionButton} onClick={() => setActiveTab('browse')}>
                  <span style={{ fontSize: '24px' }}>🔍</span>
                  <span>Browse All</span>
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            {dashboard.recent_activity.length > 0 && (
              <div style={styles.activitySection}>
                <h3 style={styles.sectionTitle}>Recent Activity</h3>
                <div style={styles.activityList}>
                  {dashboard.recent_activity.map((activity, idx) => (
                    <div key={idx} style={styles.activityItem}>
                      <div style={styles.activityAction}>{activity.action.replace(/_/g, ' ')}</div>
                      <div style={styles.activityMeta}>
                        {activity.item_title && <span>{activity.item_title}</span>}
                        <span style={{ opacity: 0.6 }}> • {new Date(activity.timestamp).toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* BROWSE ITEMS */}
        {activeTab === 'browse' && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Browse Vault</h2>
              <button style={styles.createButton} onClick={() => setShowAddForm(!showAddForm)}>
                {showAddForm ? 'CANCEL' : '+ ADD ITEM'}
              </button>
            </div>

            {/* Search & Filters */}
            <div style={styles.filters}>
              <input
                type="text"
                placeholder="Search vault..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={styles.searchInput}
              />
              <select
                value={selectedCategory || ''}
                onChange={(e) => setSelectedCategory(e.target.value || null)}
                style={styles.filterSelect}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.name}>{cat.icon} {cat.name}</option>
                ))}
              </select>
            </div>

            {/* Add Item Form */}
            {showAddForm && (
              <form onSubmit={handleAddItem} style={styles.form}>
                <input name="title" placeholder="Title" required style={styles.input} />
                <textarea name="description" placeholder="Description" required style={styles.textarea} rows={3} />
                <select name="category" required style={styles.input}>
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
                <select name="item_type" required style={styles.input}>
                  <option value="">Select Type</option>
                  <option value="memory">💭 Memory</option>
                  <option value="photo">📸 Photo</option>
                  <option value="document">📄 Document</option>
                  <option value="video">🎬 Video</option>
                  <option value="audio">🎵 Audio</option>
                  <option value="note">📝 Note</option>
                  <option value="legal">⚖️ Legal</option>
                  <option value="financial">💰 Financial</option>
                </select>
                <select name="visibility" style={styles.input}>
                  <option value="family">Family</option>
                  <option value="legacy">Legacy (Future)</option>
                  <option value="private">Private</option>
                  <option value="admin">Admin Only</option>
                </select>
                <input name="tags" placeholder="Tags (comma separated)" style={styles.input} />
                <button type="submit" style={styles.submitButton}>ADD TO VAULT</button>
              </form>
            )}

            {/* Items Grid */}
            <div style={styles.itemsGrid}>
              {items.map(item => (
                <div key={item.id} style={styles.itemCard}>
                  <div style={styles.itemIcon}>{getItemTypeIcon(item.item_type)}</div>
                  <div style={styles.itemTitle}>{item.title}</div>
                  <div style={styles.itemDesc}>{item.description}</div>
                  <div style={styles.itemMeta}>
                    <span style={styles.itemCategory}>{item.category}</span>
                    <span style={styles.itemDate}>{new Date(item.date_added).toLocaleDateString()}</span>
                  </div>
                  {item.tags.length > 0 && (
                    <div style={styles.itemTags}>
                      {item.tags.map((tag, idx) => (
                        <span key={idx} style={styles.tag}>{tag}</span>
                      ))}
                    </div>
                  )}
                  {item.file_id && (
                    <button
                      style={styles.downloadButton}
                      onClick={() => handleDownload(item.id, item.title)}
                    >
                      DOWNLOAD
                    </button>
                  )}
                </div>
              ))}
              {items.length === 0 && (
                <div style={styles.empty}>No items found. Add your first memory!</div>
              )}
            </div>
          </div>
        )}

        {/* CATEGORIES */}
        {activeTab === 'categories' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Vault Categories</h2>
            <div style={styles.categoriesGrid}>
              {categories.map(cat => (
                <div
                  key={cat.id}
                  style={{ ...styles.categoryCard, borderColor: cat.color }}
                  onClick={() => {
                    setSelectedCategory(cat.name);
                    setActiveTab('browse');
                  }}
                >
                  <div style={{ fontSize: '48px', marginBottom: '15px' }}>{cat.icon}</div>
                  <div style={styles.categoryName}>{cat.name}</div>
                  <div style={styles.categoryDesc}>{cat.description}</div>
                  <div style={styles.categoryCount}>{cat.item_count} items</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TIMELINE */}
        {activeTab === 'timeline' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Family Legacy Timeline</h2>
            <div style={styles.timeline}>
              {items
                .filter(item => item.legacy_date)
                .sort((a, b) => new Date(a.legacy_date!).getTime() - new Date(b.legacy_date!).getTime())
                .map(item => (
                  <div key={item.id} style={styles.timelineItem}>
                    <div style={styles.timelineDate}>{new Date(item.legacy_date!).toLocaleDateString()}</div>
                    <div style={styles.timelineContent}>
                      <div style={styles.timelineTitle}>{getItemTypeIcon(item.item_type)} {item.title}</div>
                      <div style={styles.timelineDesc}>{item.description}</div>
                    </div>
                  </div>
                ))}
              {items.filter(item => item.legacy_date).length === 0 && (
                <div style={styles.empty}>No timeline items yet. Add legacy dates to your memories!</div>
              )}
            </div>
          </div>
        )}

        {/* SECURE RECORDS */}
        {activeTab === 'secure' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Secure Records</h2>
            <p style={styles.secureWarning}>
              🔒 This section contains sensitive legal and financial documents.
              Access is restricted based on your role.
            </p>
            <div style={styles.itemsGrid}>
              {items
                .filter(item => item.item_type === 'legal' || item.item_type === 'financial')
                .map(item => (
                  <div key={item.id} style={{ ...styles.itemCard, borderColor: '#ff4444' }}>
                    <div style={styles.itemIcon}>{getItemTypeIcon(item.item_type)}</div>
                    <div style={styles.itemTitle}>{item.title}</div>
                    <div style={styles.itemDesc}>{item.description}</div>
                    <div style={styles.itemMeta}>
                      <span style={{ ...styles.itemCategory, color: '#ff4444' }}>{item.item_type.toUpperCase()}</span>
                      <span style={styles.itemDate}>{new Date(item.date_added).toLocaleDateString()}</span>
                    </div>
                    {item.file_id && (
                      <button
                        style={{ ...styles.downloadButton, background: '#ff4444' }}
                        onClick={() => handleDownload(item.id, item.title)}
                      >
                        DOWNLOAD
                      </button>
                    )}
                  </div>
                ))}
              {items.filter(item => item.item_type === 'legal' || item.item_type === 'financial').length === 0 && (
                <div style={styles.empty}>No secure records yet.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Styles
const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100%',
    height: '100%',
    background: 'black',
    color: '#FFD700',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '30px 40px',
    borderBottom: '1px solid rgba(255, 215, 0, 0.3)',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    opacity: 0.7,
  },
  backButton: {
    padding: '12px 24px',
    background: 'rgba(255, 215, 0, 0.1)',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  error: {
    padding: '15px 40px',
    background: 'rgba(255, 0, 0, 0.1)',
    border: '1px solid #ff4444',
    color: '#ff4444',
  },
  tabBar: {
    display: 'flex',
    gap: '10px',
    padding: '20px 40px',
    borderBottom: '1px solid rgba(255, 215, 0, 0.2)',
    overflowX: 'auto',
  },
  tab: {
    padding: '10px 20px',
    background: 'transparent',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    color: '#FFD700',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
  },
  tabActive: {
    background: 'rgba(255, 215, 0, 0.2)',
    borderColor: '#FFD700',
  },
  content: {
    flex: 1,
    padding: '40px',
    overflowY: 'auto',
  },
  dashboardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '20px',
    marginBottom: '40px',
  },
  card: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    padding: '30px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  cardIcon: {
    fontSize: '48px',
    marginBottom: '15px',
  },
  cardNumber: {
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  cardLabel: {
    fontSize: '14px',
    opacity: 0.7,
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  quickActions: {
    marginBottom: '40px',
  },
  sectionTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '20px',
  },
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '15px',
  },
  actionButton: {
    padding: '20px',
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    color: '#FFD700',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  activitySection: {
    marginTop: '40px',
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  activityItem: {
    padding: '15px',
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.2)',
    borderRadius: '4px',
  },
  activityAction: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '5px',
    textTransform: 'capitalize',
  },
  activityMeta: {
    fontSize: '12px',
    opacity: 0.7,
  },
  section: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
  },
  createButton: {
    padding: '12px 24px',
    background: '#FFD700',
    color: 'black',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  filters: {
    display: 'flex',
    gap: '15px',
    marginBottom: '30px',
  },
  searchInput: {
    flex: 1,
    padding: '12px',
    background: 'black',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
    outline: 'none',
    fontSize: '14px',
  },
  filterSelect: {
    padding: '12px',
    background: 'black',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
    outline: 'none',
    fontSize: '14px',
    minWidth: '200px',
  },
  form: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '30px',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  input: {
    padding: '12px',
    background: 'black',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
    outline: 'none',
    fontSize: '14px',
  },
  textarea: {
    padding: '12px',
    background: 'black',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
    outline: 'none',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  submitButton: {
    padding: '14px',
    background: '#FFD700',
    color: 'black',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  itemsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '20px',
  },
  itemCard: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  itemIcon: {
    fontSize: '32px',
  },
  itemTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
  },
  itemDesc: {
    fontSize: '14px',
    opacity: 0.8,
  },
  itemMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    opacity: 0.7,
  },
  itemCategory: {
    fontWeight: 'bold',
  },
  itemDate: {
    opacity: 0.6,
  },
  itemTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  tag: {
    padding: '4px 8px',
    background: 'rgba(255, 215, 0, 0.1)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '3px',
    fontSize: '11px',
  },
  downloadButton: {
    padding: '10px',
    background: '#FFD700',
    color: 'black',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold',
    marginTop: '10px',
  },
  categoriesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '20px',
  },
  categoryCard: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '2px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    padding: '30px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  categoryName: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  categoryDesc: {
    fontSize: '13px',
    opacity: 0.7,
    marginBottom: '15px',
  },
  categoryCount: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#FFD700',
  },
  timeline: {
    position: 'relative',
    paddingLeft: '40px',
  },
  timelineItem: {
    position: 'relative',
    marginBottom: '30px',
    paddingLeft: '30px',
  },
  timelineDate: {
    fontSize: '12px',
    fontWeight: 'bold',
    marginBottom: '10px',
    color: '#FFD700',
  },
  timelineContent: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    padding: '15px',
  },
  timelineTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  timelineDesc: {
    fontSize: '14px',
    opacity: 0.8,
  },
  secureWarning: {
    padding: '15px',
    background: 'rgba(255, 0, 0, 0.1)',
    border: '1px solid rgba(255, 0, 0, 0.3)',
    borderRadius: '4px',
    marginBottom: '30px',
    fontSize: '14px',
  },
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
    opacity: 0.7,
    fontSize: '16px',
    gridColumn: '1 / -1',
  },
};
